import React, { FC, useState, useMemo } from 'react'
import { Menu, MenuItem } from '@mui/material'
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-premium'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { Gateway, GatewayState } from 'src/api'
import { useAssignGatewayRequest } from 'src/hooks/api'
import { useAddPopupMessage, useDialog, useGatewayLabel, useGatewayStateUpdate } from 'src/hooks/ui'
import { DialogNames, GatewayStateList } from 'src/enums'
import { AssignSensorToContractor, StatefulDialog, MakeAvailableSensorModal, MarkLostSensorModal } from 'src/components'

export enum GatewayMenuItemKey {
  DownloadLabel = 'downloadLabel',
  ReadyToUse = 'readyToUse',
  MarkAsLost = 'markAsLost',
  MakeAvailable = 'makeAvailable',
  Assign = 'assign',
  Reassign = 'reassign'
}

interface GatewayContextMenuProps {
  menuItems: GatewayMenuItemKey[]
  currentRow: Gateway | null
  menuAnchorEl: HTMLElement | null
  onOpenMenu: (event: React.MouseEvent<HTMLElement>, row: Gateway) => void
  onCloseMenu: () => void
  states: GatewayState[]
  refetchGatewayList: () => void
}

export const GatewayContextMenu: FC<GatewayContextMenuProps> = ({ menuItems, currentRow, menuAnchorEl, onOpenMenu, onCloseMenu, states, refetchGatewayList }) => {
  const { generatingLabel, downloadLabel } = useGatewayLabel()
  const addPopupMessage = useAddPopupMessage()
  const {
    openGatewayMarkLostDialog,
    closeGatewayMarkLostDialog,
    openGatewayMakeAvailableDialog,
    closeGatewayMakeAvailableDialog,
    updateGatewayState,
    isLoadingChangingGatewayState
  } = useGatewayStateUpdate()
  const { openDialog, closeDialog } = useDialog(DialogNames.AssignSensorToContractor)
  const [gatewayId, setGatewayId] = useState<number | null>(null)

  const assignGatewayRequest = useAssignGatewayRequest({
    options: {
      onSettled: () => closeDialog(),
      onSuccess: () => {
        refetchGatewayList()
        onCloseMenu()
      }
    }
  })

  const availableState = useMemo(() => states.find((state) => state.name === GatewayStateList.Available), [states])
  const newState = useMemo(() => states.find((state) => state.name === GatewayStateList.New), [states])
  const lostState = useMemo(() => states.find((state) => state.name === GatewayStateList.Lost), [states])
  const assignedState = useMemo(() => states.find((state) => state.name === GatewayStateList.Assigned), [states])

  const isLost = useMemo(() => currentRow && currentRow.stateId === lostState?.id, [currentRow, lostState])
  const disabledReadyToUse = !availableState || isLoadingChangingGatewayState

  const assignGateway = (contractorId: number | null) => {
    if (gatewayId !== null) {
      assignGatewayRequest.mutate(
        {
          gatewayId,
          contractorId
        },
        {
          onError: (error: any) => {
            const errorMessage = error?.message || 'Failed to assign gateway.'
            addPopupMessage({ text: errorMessage, type: 'error' })
          }
        }
      )
    }
  }

  const handleDownloadLabel = () => {
    if (currentRow?.deviceId) {
      downloadLabel(currentRow.deviceId)
      onCloseMenu()
    }
  }

  const handleReadyToUse = () => {
    if (!disabledReadyToUse && currentRow && availableState) {
      updateGatewayState({
        gatewayId: currentRow.id,
        state: availableState as GatewayState,
        onSuccess: () => {
          refetchGatewayList()
          onCloseMenu()
        }
      })
    }
  }

  const handleMarkAsLost = () => {
    openGatewayMarkLostDialog()
  }

  const handleMakeAvailable = () => {
    openGatewayMakeAvailableDialog()
  }

  const handleAssign = () => {
    if (currentRow) {
      setGatewayId(currentRow.id)
      openDialog()
      onCloseMenu()
    }
  }

  const handleReassign = () => {
    if (assignedState && currentRow) {
      setGatewayId(currentRow.id)
      openDialog()
      onCloseMenu()
    }
  }

  const renderMenuItem = (key: GatewayMenuItemKey) => {
    switch (key) {
      case GatewayMenuItemKey.DownloadLabel:
        return (
          <MenuItem key={key} disabled={generatingLabel} onClick={handleDownloadLabel}>
            Download label
          </MenuItem>
        )
      case GatewayMenuItemKey.ReadyToUse:
        return (
          <MenuItem key={key} disabled={disabledReadyToUse} onClick={handleReadyToUse}>
            Ready to use
          </MenuItem>
        )
      case GatewayMenuItemKey.MarkAsLost:
        return (
          <MenuItem key={key} disabled={isLoadingChangingGatewayState} onClick={handleMarkAsLost}>
            Mark as lost
          </MenuItem>
        )
      case GatewayMenuItemKey.MakeAvailable:
        return (
          <MenuItem key={key} disabled={isLoadingChangingGatewayState} onClick={handleMakeAvailable}>
            Make available
          </MenuItem>
        )
      case GatewayMenuItemKey.Assign:
        return (
          <MenuItem key={key} disabled={!assignedState} onClick={handleAssign}>
            Assign
          </MenuItem>
        )
      case GatewayMenuItemKey.Reassign:
        return (
          <MenuItem key={key} disabled={!assignedState} onClick={handleReassign}>
            Reassign
          </MenuItem>
        )
      default:
        return null
    }
  }

  const getMenuItems = () => {
    if (menuItems.includes(GatewayMenuItemKey.MarkAsLost) && menuItems.includes(GatewayMenuItemKey.MakeAvailable)) {
      if (!isLost) {
        return menuItems.filter((key) => key !== GatewayMenuItemKey.MakeAvailable).map((key) => renderMenuItem(key))
      } else {
        return menuItems.filter((key) => key !== GatewayMenuItemKey.MarkAsLost).map((key) => renderMenuItem(key))
      }
    }

    return menuItems.map((key) => renderMenuItem(key))
  }

  return (
    <>
      <Menu anchorEl={menuAnchorEl} open={!!menuAnchorEl} onClose={onCloseMenu}>
        {getMenuItems()}
      </Menu>
      {menuItems.includes(GatewayMenuItemKey.MarkAsLost) && (
        <MarkLostSensorModal
          sensorDeviceId={currentRow?.deviceId as string}
          isLoading={isLoadingChangingGatewayState}
          deviceType="gateway"
          onCancel={() => {
            closeGatewayMarkLostDialog()
            onCloseMenu()
          }}
          onSubmit={() => {
            if (currentRow && lostState) {
              updateGatewayState({
                gatewayId: currentRow.id,
                state: lostState as GatewayState,
                onSuccess: () => {
                  refetchGatewayList()
                  onCloseMenu()
                }
              })
            }
          }}
        />
      )}
      {menuItems.includes(GatewayMenuItemKey.MakeAvailable) && (
        <MakeAvailableSensorModal
          sensorDeviceId={currentRow?.deviceId as string}
          isLoading={isLoadingChangingGatewayState}
          deviceType="gateway"
          onCancel={() => {
            closeGatewayMakeAvailableDialog()
            onCloseMenu()
          }}
          onSubmit={() => {
            if (currentRow && newState) {
              updateGatewayState({
                gatewayId: currentRow.id,
                state: newState as GatewayState,
                onSuccess: () => {
                  refetchGatewayList()
                  onCloseMenu()
                }
              })
            }
          }}
        />
      )}
      {(menuItems.includes(GatewayMenuItemKey.Assign) || menuItems.includes(GatewayMenuItemKey.Reassign)) && (
        <StatefulDialog name={DialogNames.AssignSensorToContractor} wrapContent={false}>
          <AssignSensorToContractor onSubmit={assignGateway} />
        </StatefulDialog>
      )}
    </>
  )
}

export const useGatewayContextMenu = () => {
  const [currentRow, setCurrentRow] = useState<Gateway | null>(null)
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null)

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, row: Gateway) => {
    setCurrentRow(row)
    setMenuAnchorEl(event.currentTarget)
  }

  const handleCloseMenu = () => {
    setCurrentRow(null)
    setMenuAnchorEl(null)
  }

  return {
    currentRow,
    menuAnchorEl,
    handleOpenMenu,
    handleCloseMenu
  }
}

export const createActionsColumn = (handleOpenMenu: (event: React.MouseEvent<HTMLElement>, row: any) => void): GridColDef => ({
  field: 'actions',
  headerName: '',
  type: 'actions',
  width: 30,
  getActions: ({ row }) => [
    <GridActionsCellItem key="actions" icon={<MoreVertIcon />} label="Actions" className="textPrimary" onClick={(e: any) => handleOpenMenu(e, row)} color="inherit" />
  ]
})
